/**
 * MODULE: _shared/frontend-shell/src/layout/AppLayout.tsx
 * PURPOSE: Authenticated layout for legacy module routes. Module sidebar is not offered.
 * 
 * DEPENDENCIES:
 *   - react-router-dom: matchPath, useLocation, useNavigate, Link
 *   - @core/auth: useCurrentUser
 *   - @core/tenant: useTenant
 *   - ./config/nav: mainNavItems, adminNavItems
 *   - ../design-system/components/Navigation/TopBar: TopBar
 *   - @heroicons/react: Icon components
 * 
 * PUBLIC API:
 *   - AppLayout: Named export React component
 * 
 * SIDE EFFECTS:
 *   - React rendering
 *   - Navigation via useNavigate
 * 
 * AGENT SAFETY: 🟡 RESTRICTED - Layout component affecting routing
 * FILE SIZE: 397 / 500 lines (layout + shortcuts)
 * 
 * CHANGE LOG:
 * RECENT: 2026-10-09 [2.2.2163] — Hide the module sidebar, mobile module nav, and Traditional/Agentic switcher.
 * RECENT_PRIOR: 2026-08-06 [2.2.2133] — **`SubscriptionRenewBanner`** under TopBar when AI tokens low/exhausted.
 * RECENT: 2026-08-06 [2.2.2137] — TopBar mode toggle resize-safe; SubscriptionRenewBanner under TopBar.
 * RECENT_PRIOR: 2026-08-03 [2.2.2023] — afterBrand mode switcher left of brand cluster; Brazn ai TopBar parity.
 * RECENT_PRIOR: 2026-07-28 [2.2.1678] — AgenticModeSwitcher in TopBar (Traditional → Agentic).
 * RECENT_PRIOR: 2026-07-08 [2.2.1550] — **`BrainAlertsNavBadge`** in top bar beside learnings badge.
 * RECENT_PRIOR: 2026-06-21 [2.2.1458] — **`GlobalSalesTargetSetHost`** inside **`ShellAssistantProvider`**; **`make dev`** unchanged.
 * RECENT_PRIOR: 2026-06-18 [2.2.1450] — **`AIAssistantRightRail`** in-flow split on desktop; **`/langflow-assistant`** full-bleed page (no rail); **`showShellAssistantChrome`** excludes langflow + browser; **`make dev`** unchanged.
 *   - 2026-06-17 [2.2.1415]: **`/browser`** — hide shell ⌘K toggle + dock; full-bleed live view; in-page command panel only.
 *   - 2026-06-17: **`/browser`** — full-bleed live view; shell ⌘K dock hidden (use in-page command panel).
 *   - 2026-06-09: **`MobileShellProvider`** — touch + viewport detection; **`data-mobile-shell`** on **`<html>`**; sidebar hidden on phone landscape; compact main padding.
 *   - 2026-06-08: [2.2.1341] — **`ShellContentWithAssistant`**: full-bleed split column; resizable bottom assistant frame; **`p-0`** main when split.
 *   - 2026-06-08: [2.2.1340] — Inline **`AIAssistantDock`** + **`ShellAssistantBottomBar`** (Page | Assistant) on every shell route.
 *   - 2026-06-08: [2.2.1335] — **`ShellTopBarInsetProvider`** + **`ShellTopBarInsetSlot`** for route-level TopBar controls (Pages page insights).
 *   - 2026-06-06: [2.2.1330] — **`BrainLearningsNavBadge`** in **`TopBar`** **`beforeAvatar`** slot.
 *   - 2026-05-23: [2.2.1135] — Sidebar filters **`mainNavItems`** by **`capabilityKey`** via **`usePagesCapability`** (Pages hidden when **`capability.pages`** disabled).
 *   - 2026-05-22: #109 — **`ShellRouteTransitionOverlay`** in layout
 *   - 2026-05-20: Sprint 10 [2.2.1061] — **`MobileBottomNav`** on viewports below **`md`**; sidebar **`hidden md:flex`**; **`#main-content`** **`pb-16`** when bottom nav visible (composes with assistant **`pb-20`** when dock open — nav hidden while sheet expanded).
 *   - 2026-05-12: **`AIAssistantDock`** hidden until opened; **`#main-content`** **`pb-20`** only while sheet open, else **`pb-6`**
 *   - 2026-05-11 Task 11 #92: **`ShellAssistantLocationRouteMetaSync`** + **`#main-content`** **`pb-20`** when dock visible (clear collapsed strip); **`AIAssistantDock`** in shell
 *   - 2026-05-02: **`/calendar`** — **`#main-content`** scrolls the full calendar document (**`overflow-y-auto`**); thumb uses **`app-shell-disappearing-scroll`** (hidden until hover or focus-within), same as other shell pages.
 *   - 2026-04-30: CRM sidebar active matching uses **`isShellNavItemActive`** (`shellNavActive.ts`) so **`/crm`** matches **`/crm/*`** but not **`/crm-*`**; **`aside[data-crm-active]`** + **`data-crm-rail`** on CRM nav link soften non-CRM sidebar icons while inside CRM.
 *   - 2026-04-29: CRM shell — **`squares-2x2`** nav icon map entry; **`data-shell-crm-sub`** on **`#main-content`** under **`/crm`**; when true, outer **`<main>`** uses **`overflow-hidden`** so CRM **`CrmShell`** scroll **`role="region"`** (`#crm-main-content`) owns vertical scroll (no double scrollbar; avoids nested **`<main>`**). **`beforeAvatar`** omits **`NotificationBell`** on **`/crm`** (**`CrmHeader`** provides it). When **`crmSubShellActive`**, a second **`.skip-link`** targets **`#crm-main-content`**.
 *   - 2026-04-29 [2.2.716]: Slide-out assistant still opened via **`subscribeAssistantOpenWithPrefill`**; chart **`contextWidgetId`** and banner live in **`AiAssistantPanel`** + **`assistantPrefillBridge`** payload.
 *   - 2026-04-18: Bug 14 — **`data-shell-sales-hub-sub`** on **`#main-content`** is true only under **`/sales-hub/*`**; Activity Center is a sibling route (HubNav never mounted from AppLayout).
 *   - 2026-04-10: `app-shell-disappearing-scroll` on sidebar nav + main; nav items from `nav.ts` ([2.2.354] — Email/Presentations removed from rail). Root CHANGELOG [2.2.354].
 *   - 2026-03-03: Main content min-w-0 for flex shrink (Calendar page resizing fix). CHANGELOG [2.2.158].
 *   - 2026-02-25: Main content landmark: id="main-content" and role="main" on <main> for skip-link target. CHANGELOG [2.2.129].
 *   - 2026-02-06: FAB opens Main Assistant (/home) instead of Sales Intelligence; Cmd+J shortcut for Main Assistant;
 *                 FAB title/aria-label "Open Main Assistant (⌘J)"; Sales Intelligence remains Cmd+Shift+I
 *   - 2026-02-03: Navigation restructure - sales workflow sidebar (Assistant, Dashboard, Territory, Deals, Pipe Gen, Sales Hub);
 *                 Activity link in TopBar; GlobalSearch (⌘K); icon mappings for all nav items; rightContent search button
 *   - 2026-01-27: Redesigned sidebar to match image style - narrow width (w-20), centered icons with 
 *                 text labels below, top nav bar above sidebar, vertical centered layout
 *   - 2026-01-26: Fixed Sidebar subcomponent imports - replaced compound syntax (FlowbiteSidebar.Items) 
 *                 with separate imports (SidebarItems, SidebarItemGroup, SidebarItem) to resolve runtime 
 *                 "Element type is invalid" error. This fix ensures compatibility with flowbite-react 
 *                 component structure.
 *   - 2026-01-26: Initial Flowbite shell migration
 * 
 * See: /README.md | /ARCHITECTURE.md
 */

import { ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '@core/auth';
import { useTenant } from '@core/tenant';
import { useBrainActivityCrmQuerySync } from '@domain/pages/frontend';
import { useBrainActivityPushSubscription } from '@shared/lib/frontend/hooks/useBrainActivityPushSubscription';
import { SalesIntelligenceProvider, useSalesIntelligence } from '@domain/sales-intelligence';
import { TopBar } from '../design-system/components/Navigation/TopBar';
import { BrainAlertsNavBadge } from '../components/brain/BrainAlertsNavBadge';
import { BrainLearningsNavBadge } from '../components/brain/BrainLearningsNavBadge';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { SubscriptionRenewBanner } from '../components/billing/SubscriptionRenewBanner';
import { ToastContainer } from '../design-system/components/Feedback/ToastContainer';
import { GlobalEmailTemplateCreateHost } from '../components/templates/GlobalEmailTemplateCreateHost';
import { GlobalSalesTargetSetHost } from '../components/sales-targets/GlobalSalesTargetSetHost';
import { GlobalEmailTemplateEditHost } from '../components/templates/GlobalEmailTemplateEditHost';
import { GlobalSearch } from '../components/GlobalSearch';
import { dispatchAssistantFocus } from '../hooks/useAssistantKeyboard';
import { useMobilePushNotificationHandler } from '../hooks/useMobilePushNotificationHandler';
import { MobileShellProvider, useMobileShell } from './mobileShell';
import { AppShellDocumentTitle } from './appShellDocumentTitle';
import {
  ShellAssistantLocationRouteMetaSync,
  ShellAssistantEntityRouteBridge,
  ShellAssistantProvider,
  ShellContentWithAssistant,
  useShellAssistant,
} from './shellAssistant';
import { AssistantSelectionToolbar } from '../design-system/components/AiAssistant/AssistantSelectionToolbar';
import {
  consumeAssistantPendingPrefill,
  subscribeAssistantOpenWithPrefill,
} from '../design-system/components/AiAssistant/assistantPrefillBridge';
import { MagnifyingGlassIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { shellRouteContentKey } from './shellRouteContentKey';
import { ShellRouteTransitionOverlay } from './ShellRouteTransitionOverlay';
import {
  ShellTopBarInsetProvider,
  ShellTopBarInsetSlot,
} from './ShellTopBarInsetContext';
import { ShellTopBarMetrics } from './ShellTopBarMetrics';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  return (
    <SalesIntelligenceProvider>
      <ToastContainer>
        <GlobalEmailTemplateCreateHost />
        <GlobalEmailTemplateEditHost />
        <ShellAssistantProvider>
          <GlobalSalesTargetSetHost />
          <ShellAssistantLocationRouteMetaSync />
          <ShellAssistantEntityRouteBridge />
          <AppShellDocumentTitle />
          <MobileShellProvider>
            <AppLayoutContent>{children}</AppLayoutContent>
          </MobileShellProvider>
        </ShellAssistantProvider>
      </ToastContainer>
    </SalesIntelligenceProvider>
  );
}

function AppLayoutContent({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openSalesIntelligence } = useSalesIntelligence();
  const shellAssistant = useShellAssistant();
  const { user } = useCurrentUser();
  const { currentTenant } = useTenant();
  const [searchOpen, setSearchOpen] = useState(false);
  const [docTitle, setDocTitle] = useState(() =>
    typeof document !== 'undefined' ? document.title : ''
  );
  const isLangflowFullPage =
    location.pathname === '/langflow-assistant' || location.pathname === '/assistant';

  useBrainActivityCrmQuerySync(true);
  useBrainActivityPushSubscription({
    tenantId: currentTenant?.id ?? null,
    enabled: Boolean(currentTenant?.id),
  });

  const pageContext = useMemo(
    () => ({
      path: `${location.pathname}${location.search}`,
      title: docTitle,
    }),
    [location.pathname, location.search, docTitle]
  );

  const shellAssistantRef = useRef(shellAssistant);
  shellAssistantRef.current = shellAssistant;
  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;

  useEffect(() => {
    const id = requestAnimationFrame(() => setDocTitle(document.title));
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search]);

  /** Bug 14: Sales Hub secondary chrome (HubNav) is only mounted under `/sales-hub/*` via SalesEnablementHub — not here. */
  const salesHubSubShellActive =
    Boolean(matchPath({ path: '/sales-hub/*', end: false }, location.pathname)) ||
    location.pathname === '/sales-hub' ||
    location.pathname === '/sales-hub/';

  const crmSubShellActive =
    Boolean(matchPath({ path: '/crm/*', end: false }, location.pathname)) ||
    location.pathname === '/crm' ||
    location.pathname === '/crm/';

  const browserSubShellActive =
    Boolean(matchPath({ path: '/browser/*', end: false }, location.pathname)) ||
    location.pathname === '/browser' ||
    location.pathname === '/browser/';

  /** Calendar (and full-screen event editor): show a persistent main scrollbar — avoids hover-only thumb on long pages. */
  const calendarMainScrollActive =
    Boolean(matchPath({ path: '/calendar/*', end: false }, location.pathname)) ||
    location.pathname === '/calendar' ||
    location.pathname === '/calendar/';

  useEffect(() => {
    const OPEN_GLOBAL_SEARCH_EVENT = 'brazn-open-global-search';
    const openSearch = () => setSearchOpen(true);
    window.addEventListener(OPEN_GLOBAL_SEARCH_EVENT, openSearch);
    return () => window.removeEventListener(OPEN_GLOBAL_SEARCH_EVENT, openSearch);
  }, []);

  // Keyboard: Cmd+K = bottom assistant dock (toggle), Cmd+Shift+K = Global Search, Cmd+Shift+I = Sales Intelligence
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // CRM shell owns plain ⌘K / Ctrl+K on /crm/* (command palette); assistant uses other routes.
        const onCrmRoute =
          typeof window !== 'undefined' &&
          (window.location.pathname === '/crm' || window.location.pathname.startsWith('/crm/'));
        if (!e.shiftKey && onCrmRoute) {
          return;
        }
        e.preventDefault();
        if (e.shiftKey) {
          setSearchOpen((prev) => !prev);
        } else {
          if (isLangflowFullPage) {
            dispatchAssistantFocus();
          } else {
            const sh = shellAssistantRef.current;
            const s = sh.session;
            if (s.dockExpanded && s.panelOpen) {
              sh.collapseDock();
            } else {
              sh.openGlobalAssistant(pageContextRef.current);
            }
          }
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        openSalesIntelligence();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openSalesIntelligence, isLangflowFullPage]);

  useEffect(() => {
    return subscribeAssistantOpenWithPrefill(() => {
      // Full-page assistant routes embed AiAssistantPanel; rail must not consume prefill first.
      const path = window.location.pathname;
      if (path === '/langflow-assistant' || path === '/assistant') return;
      const pre = consumeAssistantPendingPrefill();
      shellAssistantRef.current.openGlobalAssistant(pageContextRef.current, {
        prefillContext: pre?.markdown ?? '',
        autoSend: pre?.autoSend,
      });
    });
  }, []);

  // URL sync: ?salesIntel=open&company=Stripe opens modal with prefill
  useEffect(() => {
    const salesIntel = searchParams.get('salesIntel');
    if (salesIntel === 'open') {
      const company = searchParams.get('company');
      openSalesIntelligence({ company: company ?? undefined });
      const next = new URLSearchParams(searchParams);
      next.delete('salesIntel');
      next.delete('company');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount when URL has param
  }, []);

  const { isMobileShell } = useMobileShell();
  useMobilePushNotificationHandler();
  const showShellAssistantChrome =
    !browserSubShellActive && !isLangflowFullPage;

  const assistantSplitActive =
    showShellAssistantChrome &&
    shellAssistant.session.dockExpanded &&
    shellAssistant.session.panelOpen;

  const mainShellClassName = (() => {
    const browserFullBleed = browserSubShellActive && !assistantSplitActive;
    const scroll =
      isLangflowFullPage ||
      crmSubShellActive ||
      salesHubSubShellActive ||
      browserFullBleed
        ? 'overflow-x-hidden overflow-y-hidden'
        : 'overflow-y-auto';
    const inset =
      isLangflowFullPage || assistantSplitActive
        ? 'p-0'
        : isMobileShell
          ? 'px-3 pt-4 pb-3'
          : 'px-6 pt-6 pb-4';
    return `app-shell-disappearing-scroll relative flex min-h-0 flex-1 min-w-0 flex-col ${inset} ${scroll}`;
  })();

  return (
    <ShellTopBarInsetProvider>
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <ShellTopBarMetrics />
      <ShellRouteTransitionOverlay />
      {crmSubShellActive ? (
        <a href="#crm-main-content" className="skip-link">
          Skip to CRM content
        </a>
      ) : null}
      {/* Top navbar */}
      <TopBar
        user={user ? {
          name: user.name,
          email: user.email,
          avatar_url: user.avatar_url,
        } : undefined}
        beforeAvatar={
          <>
            {user?.role === 'super_admin' ? (
              <button
                type="button"
                onClick={() => navigate('/super-admin')}
                className="rounded-lg px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Admin
              </button>
            ) : null}
            <ShellTopBarInsetSlot />
            <BrainAlertsNavBadge />
            <BrainLearningsNavBadge />
            {crmSubShellActive ? null : <NotificationBell />}
          </>
        }
        tenantName={currentTenant?.name}
        onActivityClick={() => navigate('/activities')}
        rightContent={
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className={`${
                isMobileShell
                  ? 'inline-flex min-h-[44px] min-w-[44px] items-center justify-center'
                  : 'p-2'
              } text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors`}
              title="Search (⌘⇧K)"
              aria-label="Search"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
            {!browserSubShellActive && !isLangflowFullPage && (
              <button
                type="button"
                onClick={() => {
                  const s = shellAssistant.session;
                  if (s.dockExpanded && s.panelOpen) {
                    shellAssistant.collapseDock();
                  } else {
                    shellAssistant.openGlobalAssistant(pageContext);
                  }
                }}
                className={`${
                  isMobileShell
                    ? 'inline-flex min-h-[44px] min-w-[44px] items-center justify-center'
                    : 'p-2'
                } rounded-lg transition-colors ${
                  shellAssistant.session.dockExpanded && shellAssistant.session.panelOpen
                    ? 'bg-pink-100 text-pink-600 dark:bg-pink-950/55 dark:text-pink-300'
                    : 'text-pink-500 hover:bg-pink-50 hover:text-pink-600 dark:text-pink-400 dark:hover:bg-pink-950/40 dark:hover:text-pink-300'
                }`}
                title="AI Assistant dock (⌘K)"
                aria-label="Toggle AI Assistant dock (⌘K)"
                data-testid="shell-topbar-assistant-toggle"
              >
                <SparklesIcon className="h-5 w-5" aria-hidden />
              </button>
            )}
          </div>
        }
      />
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <SubscriptionRenewBanner />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <ShellContentWithAssistant
          pageContext={pageContext}
          pageTitle={docTitle}
          showAssistantChrome={showShellAssistantChrome}
          showMobileBottomNav={false}
          isMobileShell={isMobileShell}
          mainClassName={mainShellClassName}
          contentKey={shellRouteContentKey(location.pathname)}
          salesHubSubShellActive={salesHubSubShellActive}
          crmSubShellActive={crmSubShellActive}
          calendarMainScrollActive={calendarMainScrollActive}
        >
          {children}
        </ShellContentWithAssistant>
      </div>
      {!isLangflowFullPage && <AssistantSelectionToolbar />}
    </div>
    </ShellTopBarInsetProvider>
  );
}
